import React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import api from '@/utils/api';
import tw from '@/styles/tailwind';
import Text from '@/components/Text';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';

// Columnas de solo lectura (metadata importada de la base de entrega Vinte).
const COLS: { label: string; w: number; get: (u: any) => any }[] = [
  { label: 'Fase', w: 64, get: u => u.vinte?.fase },
  { label: 'Etapa', w: 64, get: u => u.vinte?.etapa },
  // { label: 'Programa', w: 90, get: u => u.vinte?.programa },
  { label: 'Privada / Calle', w: 150, get: u => u.street_name },
  { label: 'No. Oficial', w: 90, get: u => u.external_number },
  { label: 'Mza', w: 56, get: u => u.vinte?.mza },
  { label: 'Lote', w: 56, get: u => u.vinte?.lote },
  { label: 'Unidad', w: 70, get: u => u.internal_number },
  { label: 'Edificio', w: 80, get: u => u.vinte?.edificio },
  { label: 'Prototipo', w: 130, get: u => u.vinte?.prototipo },
  // { label: 'Plaza', w: 96, get: u => u.vinte?.plaza },
  // { label: 'Fraccionamiento', w: 130, get: u => u.vinte?.fraccionamiento },
];

// Fechas editables inline (se guardan en vinte_params).
const DATE_COLS: { key: string; label: string; w: number }[] = [
  { key: 'fecha_entrega_ac', label: 'Entrega AC', w: 150 },
  { key: 'fecha_entrega_admin', label: 'Entrega Admin', w: 150 },
  { key: 'fecha_entrega_cliente', label: 'Entrega Cliente', w: 150 },
];

const ROW_W = COLS.reduce((s, c) => s + c.w, 0) + DATE_COLS.reduce((s, c) => s + c.w, 0) + 30;

// Input de fecha nativo del navegador (esta pantalla es solo web).
function WebDateInput({ value, onChange }: { value?: string | null; onChange: (v: string | null) => void }) {
  return React.createElement('input', {
    type: 'date',
    value: value ?? '',
    onChange: (e: any) => onChange(e.target.value || null),
    style: {
      border: '1px solid #e5e7eb',
      borderRadius: 6,
      padding: '4px 6px',
      fontSize: 13,
      width: '100%',
      background: 'white',
      color: '#111827',
    },
  });
}

function Cell({ w, children, style }: { w: number; children?: React.ReactNode; style?: any }) {
  return (
    <View style={tw.style(`px-2 py-2 justify-center border-r border-gray-100`, { width: w }, style)}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Text style={tw`text-xs text-secondary`} numberOfLines={1}>{children}</Text>
      ) : (
        children ?? <Text style={tw`text-xs text-gray-300`}>—</Text>
      )}
    </View>
  );
}

export default function VinteUnitsScreen() {
  const { communityId } = useActiveAdminCommunity();
  const queryClient = useQueryClient();
  const queryKey = ['vinte.units', communityId];

  const { data: units, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get(`/vinte/communities/${communityId}/units`).then(r => r.data),
    enabled: !!communityId,
  });

  const { mutate: saveDate } = useMutation({
    mutationFn: ({ uuid, field, value }: { uuid: string; field: string; value: string | null }) =>
      api.patch(`/vinte/communities/${communityId}/units/${uuid}`, { [field]: value }).then(r => r.data),
    // Optimista: reflejar el cambio en la celda al instante.
    onMutate: async ({ uuid, field, value }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<any[]>(queryKey);
      queryClient.setQueryData<any[]>(queryKey, (old = []) =>
        old.map(u => (u.uuid === uuid ? { ...u, vinte: { ...u.vinte, [field]: value } } : u)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(queryKey, ctx.prev),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const rows: any[] = units ?? [];

  if (isLoading) {
    return (
      <View style={tw`flex-1 items-center justify-center`}>
        <ActivityIndicator color={tw.color('secondary')} />
      </View>
    );
  }

  return (
    <View style={tw`flex-1 bg-white rounded-xl overflow-hidden`}>
      <View style={tw`px-4 py-3 border-b border-gray-100`}>
        <Text style={tw`text-secondary font-600`}>Viviendas</Text>
        <Text style={tw`text-gray-400 text-xs`}>{rows.length} registros</Text>
      </View>

      <ScrollView horizontal>
        <View style={{ width: ROW_W }}>
          {/* Header */}
          <View style={tw`row bg-gray-50 border-b border-gray-200`}>
            {COLS.map(c => (
              <Cell key={c.label} w={c.w}>
                <Text style={tw`text-xs text-gray-500 font-600`} numberOfLines={1}>{c.label}</Text>
              </Cell>
            ))}
            {DATE_COLS.map(c => (
              <Cell key={c.key} w={c.w}>
                <Text style={tw`text-xs text-gray-500 font-600`} numberOfLines={1}>{c.label}</Text>
              </Cell>
            ))}
          </View>

          {/* Filas */}
          <ScrollView style={tw`flex-1`}>
            {rows.map((u, i) => (
              <View key={u.uuid} style={tw.style(`row border-b border-gray-100`, i % 2 === 1 && `bg-gray-50/40`)}>
                {COLS.map(c => (
                  <Cell key={c.label} w={c.w}>{c.get(u) ?? undefined}</Cell>
                ))}
                {DATE_COLS.map(c => (
                  <Cell key={c.key} w={c.w}>
                    <WebDateInput
                      value={u.vinte?.[c.key]}
                      onChange={value => saveDate({ uuid: u.uuid, field: c.key, value })}
                    />
                  </Cell>
                ))}
              </View>
            ))}
          </ScrollView>
        </View>
      </ScrollView>
    </View>
  );
}
